import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  FamilleArtisanat,
  MotifAvis,
  OrigineVente,
  Paginated,
  PeriodeVentes,
  ThemeAvis,
} from '@mirsad/shared';
import { api } from '@/lib/api';
import { telecharger } from '@/lib/telechargement';
import type { Periode } from '@/features/ventes/useVentes';

/** Un avis : une vente datée, et ce que l'analyse des avis en a tiré. */
export interface LigneAvis {
  transactionId: string;
  note: number | null;
  texte: string | null;
  /** Langue de l'acheteur, lue comme son marché. */
  langue: string | null;
  photoUrl: string | null;
  venduLe: string;
  /** `null` : article cité par l'avis sans être relevé, donc hors catalogue. */
  articleId: string | null;
  titre: string | null;
  famille: FamilleArtisanat | null;
  boutiqueId: string | null;
  boutique: string | null;
  /** Motif d'un avis à 1 ou 2 étoiles ; `null` au-delà, ou tant que l'analyse ne l'a pas lu. */
  motif: MotifAvis | null;
  themes: ThemeAvis[];
}

export interface FiltresAvis {
  periode: PeriodeVentes;
  /** Bornes au jour (AAAA-MM-JJ) qui remplacent la période, toutes deux ou aucune. */
  du?: string;
  au?: string;
  famille?: FamilleArtisanat;
  /** Note exacte. */
  note?: number;
  noteMax?: number;
  noteMin?: number;
  langue?: string;
  search?: string;
  avecPhoto?: boolean;
  origine?: OrigineVente;
  motif?: MotifAvis;
  theme?: ThemeAvis;
  exclureNumerique?: boolean;
  sortBy?: 'date' | 'note';
  sortDir?: 'asc' | 'desc';
}

/** Filtres de la répartition : tous ceux de la liste, sauf la note et le tri. */
export type FiltresRepartition = Omit<
  FiltresAvis,
  'note' | 'noteMax' | 'noteMin' | 'sortBy' | 'sortDir'
>;

export interface RepartitionAvis {
  total: number;
  /** Les cinq notes, de 5 à 1, même sans avis. */
  notes: { note: number; avis: number }[];
  avecPhoto: number;
  catalogue: number;
}

export interface CompteurAvis {
  /** Avis à 1 ou 2 étoiles des `jours` derniers jours, dans le périmètre du compte. */
  faibles: number;
  jours: number;
  depuis: string;
  calculeLe: string;
}

export interface MotifCompte {
  motif: MotifAvis;
  libelle: string;
  avis: number;
  /** Même fenêtre un an plus tôt ; `null` pour le cumul. */
  precedents: number | null;
}

export interface BoutiqueAvisFaibles {
  id: string;
  nom: string;
  faibles: number;
  avis: number;
  /** Part d'avis faibles de la boutique sur la période, en %. */
  part: number;
  note: number | null;
  /** La moitié au moins de ses avis faibles portent sur un fichier numérique. */
  numerique: boolean;
}

export interface AvisFaibles {
  periode: Periode;
  exclureNumerique: boolean;
  avis: number;
  faibles: number;
  /** Part des avis notés 1 ou 2, en %. */
  part: number;
  precedente: { avis: number; faibles: number; part: number } | null;
  septJours: number;
  trenteJours: number;
  /** Avis faibles sur un fichier numérique, comptés même quand ils sont exclus. */
  numeriques: { avis: number; precedents: number | null };
  /** Avis faibles de la période que l'analyse n'a pas encore classés. */
  enAttente: number;
  /** 24 mois civils, le dernier en cours. */
  serie: { mois: string[]; avis: number[]; faibles: number[] };
  motifs: MotifCompte[];
  boutiques: BoutiqueAvisFaibles[];
  /** Avis minimum sur la période pour qu'une boutique figure au classement. */
  seuilBoutiques: number;
}

export interface ThemeCompte {
  theme: ThemeAvis;
  libelle: string;
  avis: number;
  /** Part des avis de la colonne qui évoquent le thème, en %. */
  part: number;
}

export interface MarcheTheme {
  langue: string;
  faibles: number;
  avecTheme: number;
  part: number;
}

export interface ThemesAvis {
  periode: Periode;
  famille: FamilleArtisanat | null;
  /** Avis rédigés notés 4 ou 5. */
  positifs: { avis: number; themes: ThemeCompte[] };
  /** Avis rédigés notés 1 ou 2. */
  negatifs: { avis: number; themes: ThemeCompte[] };
  /** Poids d'un thème parmi les avis faibles de chaque marché. */
  marches: {
    theme: ThemeAvis;
    libelle: string;
    seuil: number;
    lignes: MarcheTheme[];
    /** Marchés sous le seuil, repliés. */
    replies: number;
  } | null;
  /** Avis rédigés sans famille faute d'article relevé ; `null` pour un conseiller ou une famille choisie. */
  horsCatalogue: number | null;
  enAttente: number;
}

/** Retire les paramètres vides ; un booléen faux vaut absence de filtre. */
const nettoyer = (o: object) =>
  Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== undefined && v !== '' && v !== null && v !== false),
  );

export const useListeAvis = (
  filtres: FiltresAvis,
  page: number,
  { pageSize = 20, enabled = true }: { pageSize?: number; enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: ['avis', 'liste', filtres, page, pageSize],
    queryFn: async () =>
      (
        await api.get<Paginated<LigneAvis>>('/ventes/avis', {
          params: { ...nettoyer(filtres), page, pageSize },
        })
      ).data,
    placeholderData: keepPreviousData,
    enabled,
  });

export const useRepartitionAvis = (filtres: FiltresRepartition) =>
  useQuery({
    queryKey: ['avis', 'repartition', filtres],
    queryFn: async () =>
      (await api.get<RepartitionAvis>('/ventes/avis/repartition', { params: nettoyer(filtres) }))
        .data,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

/** Compteur du menu : relu toutes les dix minutes, comme le garde l'API. */
export const useCompteurAvis = () =>
  useQuery({
    queryKey: ['avis', 'compteur'],
    queryFn: async () => (await api.get<CompteurAvis>('/ventes/avis/compteur')).data,
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
  });

export const useAvisFaibles = (params: {
  periode: PeriodeVentes;
  du?: string;
  au?: string;
  famille?: FamilleArtisanat;
  exclureNumerique?: boolean;
}) =>
  useQuery({
    queryKey: ['avis', 'faibles', params],
    queryFn: async () =>
      (await api.get<AvisFaibles>('/ventes/avis/faibles', { params: nettoyer(params) })).data,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

export const useThemesAvis = (params: {
  periode: PeriodeVentes;
  du?: string;
  au?: string;
  famille?: FamilleArtisanat;
  theme?: ThemeAvis;
}) =>
  useQuery({
    queryKey: ['avis', 'themes', params],
    queryFn: async () =>
      (await api.get<ThemesAvis>('/ventes/avis/themes', { params: nettoyer(params) })).data,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

/** Télécharge les avis filtrés au format CSV. Réservé à l'admin et à l'analyste. */
export async function exporterAvis(filtres: FiltresAvis): Promise<void> {
  await telecharger('/ventes/avis/export', nettoyer({ ...filtres }), 'artivo-avis.csv');
}
